import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { FaSave, FaFilm, FaTv, FaSync } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_API_URL || '';

const AutoSyncPage = () => {
  const [movieUrl, setMovieUrl] = useState('');
  const [seriesUrl, setSeriesUrl] = useState('');
  const [isLoadingMovies, setIsLoadingMovies] = useState(false);
  const [isLoadingSeries, setIsLoadingSeries] = useState(false);
  const [movieCount, setMovieCount] = useState(0);
  const [seriesCount, setSeriesCount] = useState(0);
  const [error, setError] = useState('');
  const [syncLog, setSyncLog] = useState(null);
  const [moviesEnabled, setMoviesEnabled] = useState(true);
  const [seriesEnabled, setSeriesEnabled] = useState(true);
  const [isSavingToggle, setIsSavingToggle] = useState(false);
  const [bouquets, setBouquets] = useState([]);
  const [movieBouquetIds, setMovieBouquetIds] = useState([]);
  const [seriesBouquetIds, setSeriesBouquetIds] = useState([]);
  const [autosyncStatus, setAutosyncStatus] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    fetchBouquets();
    fetchExistingUrls();
    fetchSyncLog();
  }, []);

  useEffect(() => {
    socketRef.current = io(BACKEND_URL, {
      query: { token: localStorage.getItem('token') }
    });

    socketRef.current.on('autosyncUpdate', (data) => {
      setAutosyncStatus(data);
      if (!data.isRunning) {
        fetchSyncLog();
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const fetchBouquets = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/movies/bouquets`);
      setBouquets(response.data || []);
    } catch (err) {
      console.error('Erro ao buscar bouquets:', err);
    }
  };

  const fetchExistingUrls = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/autosync/urls`);
      setMovieUrl(response.data.movieUrl || '');
      setSeriesUrl(response.data.seriesUrl || '');
      setMovieCount(response.data.movieCount || 0);
      setSeriesCount(response.data.seriesCount || 0);
      setMoviesEnabled(response.data.moviesEnabled !== false);
      setSeriesEnabled(response.data.seriesEnabled !== false);
      setMovieBouquetIds(
        response.data.movieBouquetIds && response.data.movieBouquetIds.length > 0
          ? response.data.movieBouquetIds
          : [1, 2]
      );
      setSeriesBouquetIds(
        response.data.seriesBouquetIds && response.data.seriesBouquetIds.length > 0
          ? response.data.seriesBouquetIds
          : [1, 2]
      );
    } catch (err) {
      setError('Erro ao buscar URLs existentes');
      console.error(err);
    }
  };

  const handleMovieBouquetToggle = (bouquetId) => {
    setMovieBouquetIds(prev =>
      prev.includes(bouquetId) ? prev.filter(id => id !== bouquetId) : [...prev, bouquetId]
    );
  };

  const handleSeriesBouquetToggle = (bouquetId) => {
    setSeriesBouquetIds(prev =>
      prev.includes(bouquetId) ? prev.filter(id => id !== bouquetId) : [...prev, bouquetId]
    );
  };

  const handleToggleAutosync = async (nextMoviesEnabled, nextSeriesEnabled) => {
    setMoviesEnabled(nextMoviesEnabled);
    setSeriesEnabled(nextSeriesEnabled);
    setIsSavingToggle(true);
    try {
      await axios.post(`${BACKEND_URL}/api/autosync/toggle`, {
        moviesEnabled: nextMoviesEnabled,
        seriesEnabled: nextSeriesEnabled
      });
    } catch (err) {
      setError('Erro ao salvar preferência de sincronização automática');
      console.error(err);
    } finally {
      setIsSavingToggle(false);
    }
  };

  const fetchSyncLog = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/autosync/log`);
      setSyncLog(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Erro ao buscar log de sincronização:', err);
    }
  };

  const formatSyncLogEntry = (entry) => {
    const date = new Date(entry.timestamp).toLocaleString('pt-BR');
    const parts = [date];

    if (entry.series?.skipped) {
      parts.push('Séries: pulado (sincronização manual em andamento)');
    } else if (entry.series?.enabled) {
      let text = `Séries: ${entry.series.newSeries} novas, ${entry.series.newEpisodes} episódios novos`;
      if (entry.series.errors) text += `, ${entry.series.errors} erros`;
      parts.push(text);
    }

    if (entry.movies?.skipped) {
      parts.push('Filmes: pulado (sincronização manual em andamento)');
    } else if (entry.movies?.enabled) {
      let text = `Filmes: ${entry.movies.newMovies} novos`;
      if (entry.movies.errors) text += `, ${entry.movies.errors} erros`;
      parts.push(text);
    }

    if (entry.status === 'error') {
      parts.push(`ERRO: ${entry.error}`);
    }

    return parts.join(' — ');
  };

  const handleSaveMovieUrl = async () => {
    if (movieBouquetIds.length === 0) {
      setError('Selecione pelo menos um bouquet para os filmes.');
      return;
    }
    setIsLoadingMovies(true);
    setError('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/autosync/movies`, {
        url: movieUrl,
        bouquetIds: movieBouquetIds
      });
      setMovieCount(response.data.movieCount);
      alert('URL de filmes salva com sucesso!');
    } catch (err) {
      setError('Erro ao salvar URL de filmes');
      console.error(err);
    } finally {
      setIsLoadingMovies(false);
    }
  };

  const handleSaveSeriesUrl = async () => {
    if (seriesBouquetIds.length === 0) {
      setError('Selecione pelo menos um bouquet para as séries.');
      return;
    }
    setIsLoadingSeries(true);
    setError('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/autosync/series`, {
        url: seriesUrl,
        bouquetIds: seriesBouquetIds
      });
      setSeriesCount(response.data.seriesCount);
      alert('URL de séries salva com sucesso!');
    } catch (err) {
      setError('Erro ao salvar URL de séries');
      console.error(err);
    } finally {
      setIsLoadingSeries(false);
    }
  };

  return (
    <div className="movie-page">
      <h1 className="movie-page__title">AutoSync</h1>
      
      {error && <p className="movie-page__error">{error}</p>}

      <div className="movie-page__card">
        <h2 className="movie-page__card-title">
          <FaSync /> Sincronização Automática
        </h2>
        <p>
          A cada 48 horas o sistema verifica as URLs abaixo e adiciona sozinho o que for
          novo. Marque o que deve ser sincronizado automaticamente:
        </p>
        <div className="movie-page__form-group">
          <label htmlFor="autosync-series-enabled">
            <input
              type="checkbox"
              id="autosync-series-enabled"
              checked={seriesEnabled}
              disabled={isSavingToggle}
              onChange={(e) => handleToggleAutosync(moviesEnabled, e.target.checked)}
            />{' '}
            <FaTv /> Sincronizar séries automaticamente
          </label>
        </div>
        <div className="movie-page__form-group">
          <label htmlFor="autosync-movies-enabled">
            <input
              type="checkbox"
              id="autosync-movies-enabled"
              checked={moviesEnabled}
              disabled={isSavingToggle}
              onChange={(e) => handleToggleAutosync(e.target.checked, seriesEnabled)}
            />{' '}
            <FaFilm /> Sincronizar filmes automaticamente
          </label>
        </div>
      </div>

      {autosyncStatus?.isRunning && (
        <div className="movie-page__card">
          <h2 className="movie-page__card-title">
            <FaSync className="icon-spin" /> Sincronização Automática em Andamento
          </h2>

          {autosyncStatus.series.enabled && (
            <div className="movie-page__form-group">
              <h3 className="movie-page__subtitle"><FaTv /> Séries</h3>
              {autosyncStatus.series.skipped ? (
                <p>Pulado (sincronização manual de séries em andamento)</p>
              ) : (
                <>
                  <div className="movie-page__progress-bar">
                    <div
                      className="movie-page__progress"
                      style={{
                        width: `${autosyncStatus.series.total ? (autosyncStatus.series.processed / autosyncStatus.series.total) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                  <p>
                    {autosyncStatus.series.processed} / {autosyncStatus.series.total} — {autosyncStatus.series.newSeries} séries novas, {autosyncStatus.series.newEpisodes} episódios novos
                    {autosyncStatus.series.errors > 0 && `, ${autosyncStatus.series.errors} erros`}
                  </p>
                  {autosyncStatus.series.currentItem && <p>Processando: {autosyncStatus.series.currentItem}</p>}
                </>
              )}
            </div>
          )}

          {autosyncStatus.movies.enabled && (
            <div className="movie-page__form-group">
              <h3 className="movie-page__subtitle"><FaFilm /> Filmes</h3>
              {autosyncStatus.movies.skipped ? (
                <p>Pulado (sincronização manual de filmes em andamento)</p>
              ) : (
                <>
                  <div className="movie-page__progress-bar">
                    <div
                      className="movie-page__progress"
                      style={{
                        width: `${autosyncStatus.movies.total ? (autosyncStatus.movies.processed / autosyncStatus.movies.total) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                  <p>
                    {autosyncStatus.movies.processed} / {autosyncStatus.movies.total} — {autosyncStatus.movies.newMovies} filmes novos
                    {autosyncStatus.movies.errors > 0 && `, ${autosyncStatus.movies.errors} erros`}
                  </p>
                  {autosyncStatus.movies.currentItem && <p>Processando: {autosyncStatus.movies.currentItem}</p>}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="movie-page__card">
        <h2 className="movie-page__card-title">URL de Filmes</h2>
        <div className="movie-page__form-group">
          <label htmlFor="movie-url">
            <FaFilm /> URL de Filmes:
          </label>
          <input
            type="text"
            id="movie-url"
            value={movieUrl}
            onChange={(e) => setMovieUrl(e.target.value)}
            placeholder="http://exemplo.com:porta/..."
          />
        </div>
        {bouquets.length > 0 && (
          <div className="movie-page__form-group">
            <label>Selecione o(s) Bouquet(s) para filmes novos:</label>
            <div className="movie-page__categories-list">
              {bouquets.map((bouquet) => (
                <div key={bouquet.id} className="movie-page__category-item">
                  <input
                    type="checkbox"
                    id={`movie-bouquet-${bouquet.id}`}
                    checked={movieBouquetIds.includes(bouquet.id)}
                    onChange={() => handleMovieBouquetToggle(bouquet.id)}
                  />
                  <label htmlFor={`movie-bouquet-${bouquet.id}`}>{bouquet.bouquet_name}</label>
                </div>
              ))}
            </div>
          </div>
        )}
        <button
          onClick={handleSaveMovieUrl}
          disabled={isLoadingMovies}
          className="movie-page__btn movie-page__btn--primary"
        >
          <FaSave /> {isLoadingMovies ? 'Salvando...' : 'Salvar'}
        </button>
        {movieCount > 0 && (
          <p className="movie-page__stats-card">Total de filmes: {movieCount}</p>
        )}
      </div>

      <div className="movie-page__card">
        <h2 className="movie-page__card-title">URL de Séries</h2>
        <div className="movie-page__form-group">
          <label htmlFor="series-url">
            <FaTv /> URL de Séries:
          </label>
          <input
            type="text"
            id="series-url"
            value={seriesUrl}
            onChange={(e) => setSeriesUrl(e.target.value)}
            placeholder="http://exemplo.com:porta/..."
          />
        </div>
        {bouquets.length > 0 && (
          <div className="movie-page__form-group">
            <label>Selecione o(s) Bouquet(s) para séries novas:</label>
            <div className="movie-page__categories-list">
              {bouquets.map((bouquet) => (
                <div key={bouquet.id} className="movie-page__category-item">
                  <input
                    type="checkbox"
                    id={`series-bouquet-${bouquet.id}`}
                    checked={seriesBouquetIds.includes(bouquet.id)}
                    onChange={() => handleSeriesBouquetToggle(bouquet.id)}
                  />
                  <label htmlFor={`series-bouquet-${bouquet.id}`}>{bouquet.bouquet_name}</label>
                </div>
              ))}
            </div>
          </div>
        )}
        <button
          onClick={handleSaveSeriesUrl}
          disabled={isLoadingSeries}
          className="movie-page__btn movie-page__btn--primary"
        >
          <FaSave /> {isLoadingSeries ? 'Salvando...' : 'Salvar'}
        </button>
        {seriesCount > 0 && (
          <p className="movie-page__stats-card">Total de séries: {seriesCount}</p>
        )}
      </div>

      {syncLog && syncLog.length > 0 && (
        <div className="movie-page__card">
          <h2 className="movie-page__card-title">
            <FaSync /> Histórico de Sincronização Automática
          </h2>
          <div className="movie-page__log-container">
            <ul className="movie-page__log-list">
              {syncLog.map((entry, idx) => (
                <li key={idx}>{formatSyncLogEntry(entry)}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoSyncPage;
